import React, { useState, useEffect } from 'react';
import { Database, Package, Users, TrendingUp, DollarSign, AlertCircle } from 'lucide-react';

// Use relative URL - will be proxied to production backend via setupProxy.js
const API_URL = process.env.REACT_APP_API_URL || '';

const TRMOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/trm/stats`);
      if (!response.ok) throw new Error('Failed to fetch TRM stats');
      const data = await response.json();
      setStats(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
        <p style={{ marginTop: '16px', color: 'var(--cares-text-label)' }}>Loading TRM data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <div style={{
          background: 'color-mix(in srgb, var(--cares-orange) 22%, var(--cares-surface-card))',
          border: '1px solid color-mix(in srgb, var(--cares-orange) 30%, var(--cares-surface-card))',
          borderRadius: '8px',
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <AlertCircle style={{ color: 'var(--cares-orange)', width: '24px', height: '24px' }} />
          <div>
            <h3 style={{ color: 'var(--cares-orange)', fontWeight: '600', marginBottom: '4px' }}>Error Loading Data</h3>
            <p style={{ color: 'var(--cares-orange)' }}>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const StatCard = ({ icon: Icon, label, value, color, bgColor }) => (
    <div style={{
      background: 'var(--cares-surface-card)',
      border: '1px solid var(--cares-line-card)',
      borderRadius: '12px',
      padding: '24px',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      transition: 'transform 0.2s, box-shadow 0.2s',
      cursor: 'pointer'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
    }}
    >
      <div style={{
        background: bgColor,
        borderRadius: '12px',
        padding: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Icon style={{ width: '32px', height: '32px', color: color }} />
      </div>
      <div style={{ flex: 1 }}>
        <p style={{ color: 'var(--cares-text-label)', fontSize: '14px', marginBottom: '4px' }}>{label}</p>
        <p style={{ fontSize: '32px', fontWeight: '700', color: 'var(--cares-text-1)' }}>{value?.toLocaleString() || 0}</p>
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px' }}>
      {/* Page Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: '700', color: 'var(--cares-text-1)', marginBottom: '8px' }}>
          Technology Reference Model v2
        </h1>
        <p style={{ color: 'var(--cares-text-label)', fontSize: '16px' }}>
          Comprehensive view of the ACF software portfolio - manufacturers, products, versions, and licenses
        </p>
      </div>

      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '24px',
        marginBottom: '32px'
      }}>
        <StatCard
          icon={Users}
          label="Total Manufacturers"
          value={stats?.total_manufacturers}
          color="var(--cares-blue)"
          bgColor="#eff6ff"
        />
        <StatCard
          icon={Package}
          label="Total Products"
          value={stats?.total_products}
          color="var(--cares-purple)"
          bgColor="#f5f3ff"
        />
        <StatCard
          icon={Database}
          label="Total Versions"
          value={stats?.total_versions}
          color="var(--cares-green)"
          bgColor="#f0fdf4"
        />
        <StatCard
          icon={TrendingUp}
          label="Active Versions"
          value={stats?.active_versions}
          color="var(--cares-amber)"
          bgColor="#fffbeb"
        />
      </div>

      {/* Quick Links */}
      <div style={{
        background: 'var(--cares-surface-card)',
        border: '1px solid var(--cares-line-card)',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ fontSize: '20px', fontWeight: '600', color: 'var(--cares-text-1)', marginBottom: '16px' }}>
          Quick Access
        </h2>
        <p style={{ color: 'var(--cares-text-label)', marginBottom: '16px' }}>
          Explore TRM data using the menu items on the left
        </p>
      </div>
    </div>
  );
};

export default TRMOverview;
